import { defineStore } from 'pinia';
import { type Effect, EffectType, getDefaultEffect } from '../models/effect';
import { Firestore, collection, doc, getDoc, getDocs, setDoc, deleteDoc, DocumentReference } from 'firebase/firestore';
import { generateEffectId } from '~/utils/idGenerator';

export interface EffectStateInterface {
  effects: Effect[];
  selectedEffect?: Effect;
  loaded: Boolean;
  loading: Boolean;
}

/** In-flight getDoc promises keyed by effect ID — prevents duplicate Firestore reads. */
const _inflight = new Map<string, Promise<Effect | null>>();

export const useEffectStore = defineStore('EffectStore', {
  state: (): EffectStateInterface => ({
    effects: [],
    selectedEffect: undefined,
    loaded: false,
    loading: false,
  }),

  getters: {
    effectById: (state) => {
      return (effectId: string) => state.effects.find(effect => effect.id === effectId);
    },
    effectsByType: (state) => {
      return (type: EffectType) => state.effects.filter(effect => effect.type === type);
    },
    getOptions: (state): Effect[] => {
			// First, sort the origins array alphabetically by the name property
			// const sortedItems = [...state.effects].sort((a, b) => {
			// 	return a.name.localeCompare(b.name);
			// });

			// return sortedItems copy;
      return [...state.effects].sort((a, b) => a.id.localeCompare(b.id));
		},
  },

  actions: {
    async fetchAll() {
			const userStore = useUserStore();
			if (userStore.isLoggedIn && !this.loaded && !this.loading) {
        this.loading = true;
				const start = performance.now();
				const { $firestore } = useNuxtApp();
				const querySnapshot = await getDocs(collection($firestore as Firestore, `/effects`));
				let items = querySnapshot.docs.map((doc) => {
					return { ...getDefaultEffect(), id: doc.id, ...doc.data() } as Effect;
				});

				items.forEach((effect) => this.addEffect(effect));
        this.loaded = true;
				console.log(`⏱️ EffectStore.fetchAll: ${items.length} effects in ${(performance.now() - start).toFixed(0)}ms`);
				return items;
			}
		},
    async fetchEffectByRef(effectRefs: DocumentReference[]): Promise<Effect[]> {
      const timerId = `${Date.now()}-${Math.random()}`;
      console.time(`⏱️ EffectStore.fetchEffectByRef[${timerId}] - ${effectRefs.length} refs`);
      const { $firestore } = useNuxtApp();

      // Get the effects from the cache
      const cachedEffects = effectRefs.map(ref => this.effects.find(effect => effect.id === ref.id)).filter((eff): eff is Effect => eff !== undefined);

      // If all requested effects are cached, return them directly
      if (cachedEffects.length === effectRefs.length) {
        console.log(`✓ EffectStore.fetchEffectByRef - all ${effectRefs.length} effects cached`);
        console.timeEnd(`⏱️ EffectStore.fetchEffectByRef[${timerId}] - ${effectRefs.length} refs`);
        return cachedEffects;
      }

      // If fetchAll() already ran, any ref not in cache is orphaned — skip individual fetches
      if (this.loaded) {
        const missingIds = effectRefs
          .filter(ref => !this.effects.find(effect => effect.id === ref.id))
          .map(ref => ref.id);
        if (missingIds.length > 0) {
          console.warn(`⚠️ EffectStore: ${missingIds.length} orphaned effectRefs (not in /effects collection): ${missingIds.join(', ')}`);
        }
        console.timeEnd(`⏱️ EffectStore.fetchEffectByRef[${timerId}] - ${effectRefs.length} refs`);
        return cachedEffects;
      }

      // Fetch the missing effects (only when fetchAll hasn't run yet).
      // Deduplicates: if another caller already started fetching the same
      // effect ID, we share the same promise instead of firing a second getDoc.
      const missingEffectRefs = effectRefs.filter(ref => !this.effects.find(effect => effect.id === ref.id));
      console.log(`⏱️ EffectStore.fetchEffectByRef - fetching ${missingEffectRefs.length} missing effects (${cachedEffects.length} cached)`);
      const fetchedEffectsPromises = missingEffectRefs.map((effectRef: DocumentReference) => {
        const id = effectRef.id;
        if (_inflight.has(id)) return _inflight.get(id)!;

        const promise = (async () => {
          const start = performance.now();
          const snapshot = await getDoc(effectRef);
          const ms = (performance.now() - start).toFixed(0);
          console.log(`  ⏱️ getDoc(${effectRef.path}) → ${ms}ms ${snapshot.exists() ? '✓' : '✗ missing'}`);
          return snapshot.exists() ? { ...getDefaultEffect(), id: snapshot.id, ...snapshot.data() } as Effect : null;
        })().finally(() => _inflight.delete(id));

        _inflight.set(id, promise);
        return promise;
      });
      const fetchedEffects = await Promise.all(fetchedEffectsPromises);

      // Add fetched effects to the store and to the return array
      for (const effect of fetchedEffects) {
        if (effect) {
          this.addEffect(effect);
          cachedEffects.push(effect);
        }
      }

      console.timeEnd(`⏱️ EffectStore.fetchEffectByRef[${timerId}] - ${effectRefs.length} refs`);
      return cachedEffects;
    },
    addEffect(newEffect: Effect) {
      // Check if an effect with the same id already exists
      const effectExists = this.effects.some(effect => effect.id === newEffect.id);
  
      // Add the new effect only if it doesn't exist
      if (!effectExists) {
          this.effects.push(newEffect);
      }
    },
    updateEffect(updatedEffect: Effect) {
      const index = this.effects.findIndex(effect => effect.id === updatedEffect.id);
      if (index !== -1) {
        this.effects[index] = updatedEffect;
      }
    },
    async upsertEffect(dat: Effect) {
      const userStore = useUserStore();
      if (userStore.isLoggedIn) {
        const { $firestore } = useNuxtApp();
        const eRef = collection($firestore as Firestore, `/effects`);
        let effectRef;

        // Check if the effect already has an ID
        if (dat.id === '-1' || dat.id === undefined || !dat.id) {
          // Generate standardized ID: <type>-<lookup>-<value>-<adjustment>
          dat.id = generateEffectId(dat.type, dat.lookup || '', dat.value || '', dat.adjustment || 0);
          console.log(`Generated new effect ID: ${dat.id}`);
          effectRef = doc(eRef, dat.id);
        } else {
          // Use existing ID
          effectRef = doc(eRef, dat.id);
        }

        // Ensure internal ID matches document ID
        const effectToSave = { ...dat, id: dat.id };

        // Save or update the effect in Firestore
        try {
          await setDoc(effectRef, effectToSave);
          this.updateEffectStore(dat);
          this.selectEffect(dat.id);
          return dat;
        } catch (error) {
          console.error('Error saving Effect: ', error);
        }
      }
    },
    updateEffectStore(dat: Effect) {
      const index = this.effects.findIndex(effect => effect.id === dat.id);

      if (index !== -1) {
        // Update existing property
        this.effects[index] = dat;
      } else {
        // Add new property
        this.effects.push(dat);
      }
    },
    removeEffect(effectId: string) {
      this.effects = this.effects.filter(effect => effect.id !== effectId);
    },
    selectEffect(effectId: string) {
      const effect = this.effects.find(effect => effect.id === effectId);
      if (effect) {
        this.selectedEffect = effect;
      }
    },
    clearSelectedEffect() {
      this.selectedEffect = undefined;
    },

    async deleteEffect(effect: Effect) {
      const userStore = useUserStore();
      if (userStore.isLoggedIn && effect.id) {
        const { $firestore } = useNuxtApp();
        const effectRef = doc($firestore as Firestore, '/effects', effect.id);
        await deleteDoc(effectRef);
        this.effects = this.effects.filter((e) => e.id !== effect.id);

        // Clear selected effect if it was deleted
        if (this.selectedEffect?.id === effect.id) {
          this.selectedEffect = undefined;
        }
      }
    },
    // Additional actions like fetchEffects, saveEffect, etc. can be added based on your application's needs
  },
});
